import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, UserPlus, MessageSquare, Mail, Copy, Cpu, Pencil } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { WinnerTrophy } from "@/components/WinnerTrophy";
import { SystemTextTab, DashboardLayoutTab, BackupTab } from "@/components/admin/SystemTabs";

export default function Admin() {
  const { t } = useT();
  return (
    <div className="space-y-6">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">{t("admin_area")}</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="admin-heading">{t("admin_heading")}</h1>
      </div>
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none p-0 h-auto w-full justify-start gap-1 overflow-x-auto">
          <TabsTrigger value="users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-users">{t("admin_tab_users")}</TabsTrigger>
          <TabsTrigger value="active" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-active">Active Agents</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-categories">{t("admin_tab_categories")}</TabsTrigger>
          <TabsTrigger value="replies" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-replies">{t("admin_tab_replies")}</TabsTrigger>
          <TabsTrigger value="assets" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-assets">{t("admin_tab_assets")}</TabsTrigger>
          <TabsTrigger value="inbound" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-inbound">{t("admin_tab_inbound")}</TabsTrigger>
          <TabsTrigger value="system" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-system">System Text</TabsTrigger>
          <TabsTrigger value="layout" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-layout">Dashboard Layout</TabsTrigger>
          <TabsTrigger value="backup" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-500 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 text-slate-600 px-4 py-2 text-sm" data-testid="admin-tab-backup">Backup &amp; Restore</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="active" className="mt-6"><ActiveAgentsTab /></TabsContent>
        <TabsContent value="categories" className="mt-6"><CategoriesTab /></TabsContent>
        <TabsContent value="replies" className="mt-6"><SavedRepliesTab /></TabsContent>
        <TabsContent value="assets" className="mt-6"><AssetsTab /></TabsContent>
        <TabsContent value="inbound" className="mt-6"><InboundEmailTab /></TabsContent>
        <TabsContent value="system" className="mt-6"><SystemTextTab /></TabsContent>
        <TabsContent value="layout" className="mt-6"><DashboardLayoutTab /></TabsContent>
        <TabsContent value="backup" className="mt-6"><BackupTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const ASSET_TYPES = [
  { id: "laptop", label: "Laptop" },
  { id: "desktop", label: "Desktop" },
  { id: "printer", label: "Printer" },
  { id: "server", label: "Server" },
  { id: "phone", label: "Phone / Mobile" },
  { id: "network", label: "Network gear" },
  { id: "other", label: "Other" },
];
const ASSET_STATUS = [
  { id: "active", label: "Active", tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" },
  { id: "repair", label: "In repair", tone: "border-amber-500/40 bg-amber-500/10 text-amber-200" },
  { id: "decommissioned", label: "Decommissioned", tone: "border-slate-500/40 bg-slate-500/10 text-slate-700" },
];

function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset_tag: "", name: "", asset_type: "laptop", organization: "", owner_id: "", serial_number: "", model: "", location: "", status: "active", warranty_until: "", notes: "" });
  const load = async () => {
    const { data } = await api.get("/assets", { params: q ? { q } : {} });
    setAssets(data);
  };
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    if (!form.asset_tag.trim() || !form.name.trim()) { toast.error("Asset tag and name are required"); return; }
    try {
      await api.post("/assets", { ...form, owner_id: form.owner_id || null });
      toast.success(`Asset ${form.asset_tag} created`);
      setForm({ asset_tag: "", name: "", asset_type: "laptop", organization: "", owner_id: "", serial_number: "", model: "", location: "", status: "active", warranty_until: "", notes: "" });
      setOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete asset ${a.asset_tag} — "${a.name}"? Linked tickets will keep the tag as historical text.`)) return;
    try { await api.delete(`/assets/${a.asset_id}`); toast.success("Asset deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="assets-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Input placeholder="Search tag, name, serial, model, owner…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="bg-white border-slate-200 rounded-sm h-10" data-testid="assets-search" />
        </div>
        <Button variant="outline" onClick={load} className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm h-10" data-testid="assets-search-btn">Search</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-500 rounded-sm h-10" data-testid="asset-create-open">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> New asset
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 max-w-2xl">
            <DialogHeader><DialogTitle>New asset</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="Asset tag (e.g. KINOS-LT-042)" value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="asset-tag-input" />
              <Input placeholder="Display name (e.g. Kevin's MacBook)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="asset-name-input" />
              <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                <SelectTrigger className="bg-white border-slate-200 rounded-sm" data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSET_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-white border-slate-200 rounded-sm" data-testid="asset-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSET_STATUS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Model (optional)" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-white border-slate-200 rounded-sm" />
              <Input placeholder="Serial number (optional)" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="bg-white border-slate-200 rounded-sm" />
              <Input placeholder="Organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="bg-white border-slate-200 rounded-sm" />
              <Input placeholder="Location (Rua Fatuhada…)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white border-slate-200 rounded-sm" />
              <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                <SelectTrigger className="bg-white border-slate-200 rounded-sm" data-testid="asset-owner-select"><SelectValue placeholder="Owner (optional)" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" placeholder="Warranty until" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} className="bg-white border-slate-200 rounded-sm" />
              <Textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border-slate-200 rounded-sm col-span-2" />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="asset-create-submit">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="assets-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div className="col-span-2">Tag</div>
          <div className="col-span-3">Name / model</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Owner</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className="divide-y divide-slate-800">
          {assets.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="assets-empty">No assets yet. Create one to start tracking KINOS hardware.</div>}
          {assets.map((a) => {
            const tone = ASSET_STATUS.find((s) => s.id === a.status)?.tone || "border-slate-500/40 bg-slate-500/10 text-slate-700";
            return (
              <div key={a.asset_id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm hover:bg-slate-100" data-testid={`asset-row-${a.asset_tag}`}>
                <div className="col-span-2 font-mono text-red-700 truncate">{a.asset_tag}</div>
                <div className="col-span-3 truncate">
                  <div className="text-slate-800 truncate">{a.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.model || "—"}</div>
                </div>
                <div className="col-span-2 text-slate-700 uppercase text-xs">{a.asset_type}</div>
                <div className="col-span-2 truncate text-slate-700">{a.owner_name || <span className="text-slate-500">—</span>}</div>
                <div className="col-span-2">
                  <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border rounded-sm ${tone}`}>{a.status}</span>
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => del(a)} className="p-1 text-slate-500 hover:text-red-400" title="Delete" data-testid={`asset-delete-${a.asset_tag}`}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InboundEmailTab() {
  const webhookUrl = `${API_BASE}/inbound-email`;
  const secret = "kinos_inbound_2026"; // matches backend env INBOUND_EMAIL_SECRET
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [activity, setActivity] = useState([]);
  const loadActivity = async () => {
    try {
      const { data } = await api.get("/inbound-activity");
      setActivity(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadActivity(); const t = setInterval(loadActivity, 15000); return () => clearInterval(t); }, []);
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Kinos-Secret": secret },
        body: JSON.stringify({
          from_email: "test.customer@example.com",
          from_name: "Test Customer",
          subject: `Inbound test · ${new Date().toLocaleTimeString()}`,
          text: "This is a self-test triggered from the KINOS Admin → Inbound Email tab. If you see this ticket in the queue, the webhook is wired correctly.",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Test failed");
      setLastResult({ ok: true, ...data, at: new Date().toISOString() });
      toast.success(`Test ticket ${data.ticket_number || "(appended)"} created`);
      loadActivity();
    } catch (e) {
      setLastResult({ ok: false, error: String(e.message || e), at: new Date().toISOString() });
      toast.error(`Test failed: ${e.message || e}`);
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="space-y-6" data-testid="inbound-email-panel">
      <div className="border border-red-500/30 bg-red-500/5 p-5 rounded-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 bg-red-500/20 flex items-center justify-center rounded-sm">
            <Mail className="h-5 w-5 text-red-700" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold">Email &rarr; Ticket</div>
            <div className="text-xs text-slate-600">Forward mail from <b className="text-red-700">service@kinos.tl</b> to this webhook and every customer email becomes a ticket automatically. Replies keeping <code className="text-red-700">[Kinos###/YY]</code> in the subject append to the existing thread.</div>
          </div>
          <Button
            onClick={sendTest}
            disabled={testing}
            className="bg-cyan-500 hover:bg-cyan-400 text-black rounded-sm h-9"
            data-testid="inbound-test-btn"
          >
            {testing ? "Sending…" : "Send test email"}
          </Button>
        </div>
        {lastResult && (
          <div
            data-testid="inbound-test-result"
            className={`mt-3 text-xs font-mono px-3 py-2 rounded-sm border ${lastResult.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-red-500/40 bg-red-500/10 text-red-200"}`}
          >
            {lastResult.ok
              ? `${new Date(lastResult.at).toLocaleTimeString()} · ${lastResult.action || "created"} · ${lastResult.ticket_number || lastResult.ticket_id}`
              : `${new Date(lastResult.at).toLocaleTimeString()} · error: ${lastResult.error}`}
          </div>
        )}
      </div>

      <div className="border border-slate-200 bg-white p-5 space-y-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Webhook URL (POST, JSON)</div>
          <div className="flex items-center gap-2">
            <Input readOnly value={webhookUrl} className="bg-white border-slate-200 rounded-sm font-mono text-xs" data-testid="inbound-webhook-url" />
            <Button variant="outline" onClick={() => copy(webhookUrl, "URL")} className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm" data-testid="inbound-copy-url">
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Header · X-Kinos-Secret</div>
          <div className="flex items-center gap-2">
            <Input readOnly value={secret} className="bg-white border-slate-200 rounded-sm font-mono text-xs" data-testid="inbound-secret" />
            <Button variant="outline" onClick={() => copy(secret, "Secret")} className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm" data-testid="inbound-copy-secret">
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">JSON body shape</div>
          <pre className="bg-white border border-slate-200 rounded-sm p-3 text-[11px] font-mono text-slate-700 overflow-auto" data-testid="inbound-body-shape">
{`{
  "from_email": "customer@company.com",
  "from_name":  "Customer Name",
  "subject":    "Wi-Fi is down [Kinos023/26]",
  "text":       "Full body of the email…",
  "ticket_number": null
}`}
          </pre>
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">How to wire it up</div>
        <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
          <li><b>Zapier / Make / Microsoft Power Automate</b> — trigger on new email at <code className="text-red-700">service@kinos.tl</code> then <i>Webhook POST</i> to the URL above with the JSON above.</li>
          <li><b>Mailgun / SendGrid Inbound Parse</b> — point the MX record for kinos.tl to your provider, add an inbound route to POST to this URL with the header above.</li>
          <li><b>Test</b> — copy the URL, use Postman/curl to POST the JSON with the <code>X-Kinos-Secret</code> header, and confirm a ticket <code>Kinos###/26</code> appears in the queue.</li>
        </ol>
      </div>

      <div className="border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Inbound Activity · last {activity.length}</div>
          <button onClick={loadActivity} className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600 hover:text-slate-900" data-testid="inbound-activity-refresh">Refresh</button>
        </div>
        <div className="border border-slate-200 rounded-sm divide-y divide-slate-800" data-testid="inbound-activity-log">
          {activity.length === 0 && (
            <div className="p-6 text-center text-xs text-slate-500" data-testid="inbound-activity-empty">
              No inbound emails yet. Fire the &quot;Send test email&quot; button to seed one.
            </div>
          )}
          {activity.map((a) => (
            <div key={a.activity_id} className="grid grid-cols-12 gap-3 px-3 py-2 text-xs items-center hover:bg-slate-100" data-testid={`inbound-activity-row-${a.activity_id}`}>
              <div className="col-span-3 font-mono text-slate-600 truncate">{new Date(a.created_at).toLocaleString()}</div>
              <div className="col-span-3 truncate">
                <div className="text-slate-800 truncate">{a.from_name || "—"}</div>
                <div className="text-[10px] text-slate-500 truncate">{a.from_email}</div>
              </div>
              <div className="col-span-4 text-slate-700 truncate">{a.subject || "(no subject)"}</div>
              <div className="col-span-2 text-right">
                <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border rounded-sm ${a.action === "error" ? "border-red-500/40 bg-red-500/10 text-red-200" : a.action === "appended" ? "border-red-500/40 bg-red-500/10 text-cyan-200" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"}`}>
                  {a.action || "?"}
                </span>
                {a.ticket_number && (
                  <div className="text-[10px] font-mono text-slate-600 mt-1">
                    {a.ticket_id ? <a href={`/tickets/${a.ticket_id}`} className="hover:text-red-700">{a.ticket_number}</a> : a.ticket_number}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const ROLE_LABEL = { end_user: "End User", agent: "Agent", team_leader: "Team Leader", admin: "Admin", manager: "Manager" };

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("email,name,role,organization,department,phone,location\nalice@example.com,Alice Marta,agent,KINOS,Support,,HQ\nbob@client.tl,Bob Silva,end_user,Client Ltd,,,\n");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "end_user", organization: "", department: "", phone: "", location: "" });
  const load = async () => {
    const [u, p] = await Promise.all([
      api.get("/users"),
      api.get("/access-requests").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setPending(p.data);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const approveRequest = async (r, role) => {
    try {
      await api.post(`/access-requests/${r.request_id}/approve`, { role });
      toast.success(`${r.email} approved as ${role}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const denyRequest = async (r) => {
    if (!window.confirm(`Deny ${r.email}? They can request again by attempting to sign in.`)) return;
    try {
      await api.delete(`/access-requests/${r.request_id}`);
      toast.success("Denied");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deny failed"); }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const parseLine = (l) => {
      // Simple CSV: no embedded quotes inside quoted fields
      const out = [];
      let cur = "", inQ = false;
      for (let i = 0; i < l.length; i++) {
        const ch = l[i];
        if (ch === '"' && !inQ) inQ = true;
        else if (ch === '"' && inQ) inQ = false;
        else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
        else cur += ch;
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = parseLine(lines[0]).map((h) => h.toLowerCase());
    return lines.slice(1).map((l) => {
      const cells = parseLine(l);
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    });
  };

  const runImport = async () => {
    const rows = parseCsv(importText);
    if (rows.length === 0) { toast.error("No rows to import"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/users/bulk-import", { rows, send_welcome: sendWelcome });
      setImportResult(data);
      toast.success(`Imported ${data.created} new · updated ${data.updated} · skipped ${data.skipped}${sendWelcome ? ` · ${data.welcomed} welcome email(s)` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (/\.(xlsx|xls|ods)$/i.test(file.name)) {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      setImportText(XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]], { blankrows: false }));
    } else {
      setImportText(await file.text());
    }
    toast.success(`Loaded ${file.name}`);
  };

  const downloadTemplate = () => {
    const csv = "email,name,role,organization,department,phone,location\ncustomer@company.tl,Customer Name,end_user,Company Name,Finance,+670 ...,Dili\nagent@kinos.tl,Agent Name,agent,KINOS,IT Support,,Dili\n";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "kinos-users-template.csv";
    a.click();
  };

  const changeRole = async (uid, role) => { await api.patch(`/users/${uid}`, { role }); toast.success("Role updated"); load(); };
  const saveAltEmails = async (u, raw) => {
    const list = raw.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    try {
      await api.patch(`/users/${u.user_id}`, { alt_emails: list });
      toast.success(list.length ? `Linked emails saved for ${u.name}` : `Linked emails removed for ${u.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save linked emails");
      load();
    }
  };

  const toggleReport = async (u, v) => {
    try {
      await api.patch(`/users/${u.user_id}`, { monthly_report: v });
      toast.success(v ? `${u.name} will receive the monthly report` : `${u.name} removed from the monthly report`);
      load();
    } catch { toast.error("Failed to update"); }
  };

  const changeDepartment = async (uid, department) => {
    try {
      await api.patch(`/users/${uid}`, { department: department || null });
      toast.success("Department updated");
      load();
    } catch { toast.error("Failed to update department"); }
  };

  const editUser = async (u) => {
    const name = window.prompt("Full name", u.name || "");
    if (name === null) return;
    const cleanName = name.trim();
    if (!cleanName) { toast.error("Full name is required"); return; }
    const password = window.prompt("New password (leave empty to keep the current password)", "");
    if (password === null) return;
    if (password && password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.patch(`/users/${u.user_id}`, { name: cleanName });
      if (password) await api.post(`/users/${u.user_id}/password`, { password });
      toast.success(password ? "User name and password updated" : "User name updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const addUser = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("Email and name required"); return; }
    try {
      await api.post("/users", form);
      toast.success(`${form.name} added`);
      setForm({ email: "", name: "", role: "end_user", organization: "", department: "", phone: "", location: "" });
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delUser = async (uid, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await api.delete(`/users/${uid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const roleCounts = users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-6 text-xs font-mono text-slate-600 flex-wrap">
          <span data-testid="count-total"><span className="text-slate-900">{users.length}</span> total</span>
          <span data-testid="count-customers"><span className="text-slate-900">{roleCounts.end_user || 0}</span> customers</span>
          <span data-testid="count-agents"><span className="text-slate-900">{roleCounts.agent || 0}</span> agents</span>
          <span data-testid="count-leaders"><span className="text-slate-900">{roleCounts.team_leader || 0}</span> leaders</span>
          <span data-testid="count-admins"><span className="text-slate-900">{roleCounts.admin || 0}</span> admins</span>
          <span data-testid="count-managers"><span className="text-slate-900">{roleCounts.manager || 0}</span> managers</span>
        </div>
        <div className="flex items-center gap-2">
        <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm" data-testid="bulk-import-open">
              <UserPlus className="h-4 w-4" strokeWidth={2} /> Import Spreadsheet
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 rounded-sm max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">Bulk import users</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-slate-600">
                Upload an Excel (.xlsx) or CSV file, or paste CSV. Headers required: <code className="text-red-700">email,name,role</code>. Optional: <code className="text-red-700">organization,department,phone,location</code>. Roles: <code className="text-slate-700">end_user · agent · team_leader · admin · manager</code>. Existing emails are updated in place (your own role is never changed). Imported people can sign in straight away with Google.
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <input type="file" accept=".csv,text/csv,.xlsx,.xls,.ods" onChange={handleCsvFile} className="text-xs text-slate-600" data-testid="bulk-import-file" />
                <button type="button" onClick={downloadTemplate} className="text-xs text-red-600 hover:underline" data-testid="bulk-import-template">Download template</button>
              </div>
              <Textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={10} className="bg-white border-slate-200 rounded-sm font-mono text-xs" data-testid="bulk-import-textarea" />
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <Switch checked={sendWelcome} onCheckedChange={setSendWelcome} data-testid="bulk-import-welcome" className="data-[state=checked]:bg-red-600" />
                Email new people a welcome message with the sign-in link
              </label>
              {importResult && (
                <div className="border border-slate-200 rounded-sm p-3 text-xs space-y-1" data-testid="bulk-import-result">
                  <div><span className="text-emerald-700">{importResult.created}</span> created · <span className="text-red-700">{importResult.updated}</span> updated · <span className="text-amber-700">{importResult.skipped}</span> skipped{importResult.welcomed ? ` · ${importResult.welcomed} welcome email(s) sent` : ""}</div>
                  {importResult.errors?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-red-300 cursor-pointer">{importResult.errors.length} error(s)</summary>
                      <ul className="mt-1 pl-4 list-disc text-red-200">
                        {importResult.errors.map((er, i) => <li key={i}>row {er.row} · {er.email} · {er.error}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setImportOpen(false)} className="rounded-sm">Close</Button>
                <Button onClick={runImport} disabled={importing} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="bulk-import-run">
                  {importing ? "Importing…" : "Import"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="add-customer-btn">
              <UserPlus className="h-4 w-4" strokeWidth={2} /> Add User
            </Button>
          </DialogTrigger>          <DialogContent className="bg-white border-slate-200 rounded-sm max-w-md">
            <DialogHeader><DialogTitle className="font-display">Add a new user</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Fld label="Full name"><Input placeholder="Alex Chen" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="new-user-name" /></Fld>
              <Fld label="Email"><Input placeholder="alex@kinos.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="new-user-email" /></Fld>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Role">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </Fld>
                <Fld label="Phone"><Input placeholder="+254…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" /></Fld>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Organization"><Input placeholder="KINOS HQ" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" /></Fld>
                <Fld label="Department"><Input placeholder="Finance" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" /></Fld>
              </div>
              <Fld label="Location"><Input placeholder="HQ Floor 3" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" /></Fld>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={addUser} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="save-new-user-btn">Add</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Email</div>
          <div className="col-span-2">Organization</div>
          <div className="col-span-2">Department</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1 text-right">Since</div>
          <div className="col-span-1 text-right">&middot;</div>
        </div>
        <div className="divide-y divide-slate-800">
          {users.map((u) => (
            <div key={u.user_id} className="grid grid-cols-12 gap-3 items-center px-5 py-3" data-testid={`user-row-${u.email}`}>
              <div className="col-span-2 text-sm truncate flex items-center gap-1 min-w-0"><span className="truncate">{u.name}</span><WinnerTrophy userId={u.user_id} /></div>
              <div className="col-span-2 text-sm text-slate-600 truncate">{u.email}</div>
              <div className="col-span-2 text-xs text-slate-600 truncate">{u.organization || "\u2014"}</div>
              <div className="col-span-2">
                <Input
                  defaultValue={u.department || ""}
                  onBlur={(e) => { if ((e.target.value || "") !== (u.department || "")) changeDepartment(u.user_id, e.target.value); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  placeholder="—"
                  data-testid={`dept-input-${u.email}`}
                  className="bg-white border-slate-200 rounded-sm h-9 text-xs"
                />
              </div>
              <div className="col-span-1">
                <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-1 text-right text-xs text-slate-500 font-mono">{new Date(u.created_at).toLocaleDateString()}</div>
              <div className="col-span-1 flex items-center justify-end gap-2">
                <button onClick={() => editUser(u)} className="text-slate-500 hover:text-red-600" title="Edit name or password" data-testid={`edit-user-${u.email}`}>
                  <Pencil className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button onClick={() => delUser(u.user_id, u.name)} className="text-slate-500 hover:text-red-400" data-testid={`delete-user-${u.email}`}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="col-span-12 flex flex-wrap items-center gap-3 -mt-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Linked emails</span>
                <Input
                  key={(u.alt_emails || []).join(",")}
                  defaultValue={(u.alt_emails || []).join(", ")}
                  onBlur={(e) => { if (e.target.value.trim() !== (u.alt_emails || []).join(", ")) saveAltEmails(u, e.target.value); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  placeholder="other@email.com, second@email.com"
                  data-testid={`alt-emails-input-${u.email}`}
                  className="flex-1 min-w-[200px] bg-white border-slate-200 rounded-sm h-8 text-xs"
                />
                {u.role !== "end_user" && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 shrink-0">
                    <Switch checked={u.role === "team_leader" || !!u.monthly_report} disabled={u.role === "team_leader"} onCheckedChange={(v) => toggleReport(u, v)} data-testid={`report-toggle-${u.email}`} className="data-[state=checked]:bg-red-600" />
                    Receives monthly report{u.role === "team_leader" ? " (team leader)" : ""}
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", subcategories: "", default_assignee_id: "" });

  const load = async () => setCats((await api.get("/categories")).data);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const agents = users.filter((u) => ["agent", "team_leader", "admin", "manager"].includes(u.role));

  const add = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    await api.post("/categories", {
      name: form.name,
      description: form.description,
      subcategories: form.subcategories.split(",").map((s) => s.trim()).filter(Boolean),
      default_assignee_id: form.default_assignee_id || null,
    });
    setForm({ name: "", description: "", subcategories: "", default_assignee_id: "" });
    toast.success("Category added"); load();
  };

  const setDefaultAgent = async (cid, uid) => {
    const cat = cats.find((c) => c.category_id === cid);
    await api.patch(`/categories/${cid}`, {
      name: cat.name, description: cat.description, subcategories: cat.subcategories || [],
      default_assignee_id: uid || null,
    });
    toast.success("Default agent set");
    load();
  };

  const del = async (id) => { if (!window.confirm("Delete category?")) return; await api.delete(`/categories/${id}`); load(); toast.success("Deleted"); };

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 bg-white p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">New Category</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="cat-name-input" />
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="cat-desc-input" />
          <Input placeholder="Subcategories (comma)" value={form.subcategories} onChange={(e) => setForm({ ...form, subcategories: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="cat-sub-input" />
          <div className="flex gap-2">
            <Select value={form.default_assignee_id} onValueChange={(v) => setForm({ ...form, default_assignee_id: v })}>
              <SelectTrigger className="bg-white border-slate-200 rounded-sm" data-testid="cat-assignee-select"><SelectValue placeholder="Default agent (optional)" /></SelectTrigger>
              <SelectContent>{agents.map((a) => (<SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>))}</SelectContent>
            </Select>
            <Button onClick={add} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="cat-add-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-3 font-mono">SLA is set per priority: Critical 15m/4h · High 30m/8h · Medium 2h/24h · Low 4h/72h</div>
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div className="col-span-3">Name</div>
          <div className="col-span-4">Subcategories</div>
          <div className="col-span-4">Default agent (auto-assign)</div>
          <div className="col-span-1 text-right">·</div>
        </div>
        <div className="divide-y divide-slate-800">
          {cats.map((c) => (
            <div key={c.category_id} className="grid grid-cols-12 gap-3 items-center px-5 py-3" data-testid={`cat-row-${c.name}`}>
              <div className="col-span-3">
                <div className="text-sm">{c.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">{c.description}</div>
              </div>
              <div className="col-span-4 text-xs text-slate-600 truncate">{(c.subcategories || []).join(" · ") || "—"}</div>
              <div className="col-span-4">
                <Select value={c.default_assignee_id || "__none__"} onValueChange={(v) => setDefaultAgent(c.category_id, v === "__none__" ? "" : v)}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid={`cat-assignee-${c.name}`}>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {agents.map((a) => (<SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={() => del(c.category_id)} className="col-span-1 text-slate-500 hover:text-red-400 justify-self-end" data-testid={`cat-del-${c.name}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SavedRepliesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "General" });

  const load = async () => setItems((await api.get("/saved-replies")).data);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title & content required"); return; }
    await api.post("/saved-replies", form);
    setForm({ title: "", content: "", category: "General" });
    setOpen(false); load(); toast.success("Saved reply added");
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/saved-replies/${id}`); load(); toast.success("Deleted"); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono text-slate-600"><span className="text-slate-900">{items.length}</span> templates</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="sr-new-btn"><MessageSquare className="h-4 w-4" /> New Reply</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 rounded-sm max-w-lg">
            <DialogHeader><DialogTitle>New saved reply</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Title (shown as chip on the composer)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="sr-title" />
              <Input placeholder="Category (e.g. Account)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="sr-category" />
              <Textarea placeholder="Reply content" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="sr-content" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={add} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="sr-save-btn">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((r) => (
          <div key={r.reply_id} className="border border-slate-200 bg-white p-4" data-testid={`sr-item-${r.reply_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-medium">{r.title}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">{r.category}</div>
              </div>
              <button onClick={() => del(r.reply_id)} className="text-slate-500 hover:text-red-400" data-testid={`sr-del-${r.reply_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>
            <div className="text-xs text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed">{r.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

function ActiveAgentsTab() {
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/active-agents");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load active agents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);
  // Tick the clock every 15s so durations update live
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(id); }, []);

  const fmtDur = (iso) => {
    if (!iso) return "\u2014";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "\u2014";
    const s = Math.max(0, Math.floor((now - t) / 1000));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };
  const fmtTs = (iso) => iso ? new Date(iso).toLocaleString() : "\u2014";
  const onlineCount = rows.filter((r) => r.online).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-600">Live &middot; auto-refresh 30s</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1 text-slate-900">Active Agents</h2>
          <p className="text-xs text-slate-500 mt-1">
            <span className="inline-flex items-center gap-1.5 mr-3"><span className="inline-block h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_6px_theme(colors.emerald.400)]" /> Online right now: <b className="text-slate-900">{onlineCount}</b></span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-slate-300" /> Offline: <b className="text-slate-900">{rows.length - onlineCount}</b></span>
          </p>
        </div>
        <Button onClick={load} variant="outline" className="border-slate-200 hover:bg-slate-100 text-xs h-9 rounded-sm" data-testid="active-agents-refresh">Refresh</Button>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div className="col-span-3">Agent</div>
          <div className="col-span-2">Role &middot; Dept</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Duration online</div>
          <div className="col-span-2">Last login</div>
          <div className="col-span-2">Last sign-out</div>
        </div>
        {loading && <div className="p-6 text-xs text-slate-500 font-mono">Loading&hellip;</div>}
        {!loading && rows.length === 0 && <div className="p-6 text-xs text-slate-500 font-mono">No staff users found.</div>}
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.user_id} className="grid grid-cols-12 gap-3 items-center px-5 py-3 hover:bg-slate-50" data-testid={`active-row-${r.email}`}>
              <div className="col-span-3 min-w-0">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-1 min-w-0"><span className="truncate">{r.name}</span><WinnerTrophy userId={r.user_id} /></div>
                <div className="text-[11px] text-slate-500 truncate">{r.email}</div>
              </div>
              <div className="col-span-2 text-xs">
                <div className="font-mono uppercase tracking-[0.15em] text-slate-700">{(r.role || "").replace("_", " ")}</div>
                <div className="text-slate-500 text-[11px] truncate">{r.department || "\u2014"}</div>
              </div>
              <div className="col-span-1">
                {r.online ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-emerald-700" data-testid={`status-${r.email}`}>
                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Online
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500" data-testid={`status-${r.email}`}>
                    <span className="inline-block h-2 w-2 rounded-full bg-slate-300" /> Offline
                  </span>
                )}
              </div>
              <div className="col-span-2 text-xs">
                {r.online && r.login_at
                  ? <span className="text-slate-900 font-mono" data-testid={`dur-${r.email}`}>{fmtDur(r.login_at)}</span>
                  : <span className="text-slate-400">&mdash;</span>}
                {r.online && r.last_active_at && (
                  <div className="text-[10px] text-slate-500 mt-0.5">last heartbeat {fmtDur(r.last_active_at)} ago</div>
                )}
              </div>
              <div className="col-span-2 text-xs text-slate-700 font-mono" data-testid={`login-${r.email}`}>{fmtTs(r.login_at)}</div>
              <div className="col-span-2 text-xs text-slate-700 font-mono" data-testid={`logout-${r.email}`}>{fmtTs(r.last_logout_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
